import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import {
  Envelope, Phone, WhatsappLogo, ShareNetwork, MapPin, Translate, House,
  Star, Copy, QrCode, X, ArrowLeft,
} from "@phosphor-icons/react";
import { api, imageUrl, formatMXN } from "../lib/api";
import { Button } from "../components/ui/button";
import { PropertyCard } from "../components/PropertyCard";

const digitsOnly = (s) => (s || "").replace(/[^0-9+]/g, "");

export function AgentPublicProfile() {
  const { slug } = useParams();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showQR, setShowQR] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/agents/${slug}`);
        if (!cancelled) setAgent(data);
      } catch (err) {
        if (!cancelled) setError(err?.response?.status === 404 ? "Este asesor no existe o cambió su URL." : "No pudimos cargar el perfil.");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const publicUrl = useMemo(() => (typeof window !== "undefined" ? `${window.location.origin}/asesor/${slug}` : `/asesor/${slug}`), [slug]);

  const shareProfile = async () => {
    const shareData = {
      title: agent?.name ? `${agent.name} — Asesor ROCAFUERTE` : "Asesor ROCAFUERTE",
      text: agent?.bio || "Conoce a este asesor inmobiliario",
      url: publicUrl,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); return; } catch { /* fallthrough */ }
    }
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Enlace copiado al portapapeles");
    } catch {
      toast.error("Copia manualmente: " + publicUrl);
    }
  };

  const copyUrl = async () => {
    try { await navigator.clipboard.writeText(publicUrl); toast.success("Enlace copiado"); }
    catch { /* ignore */ }
  };

  if (loading) return <ProfileSkeleton />;
  if (error || !agent) return (
    <div className="max-w-4xl mx-auto px-6 py-24 text-center" data-testid="agent-profile-error">
      <House size={40} className="mx-auto text-[#C8A24A]/30 mb-4" />
      <p className="font-heading text-2xl">{error || "Asesor no encontrado"}</p>
      <Link to="/" className="text-sm text-[#C8A24A]/70 hover:text-[#C8A24A] mt-4 inline-block">← Volver al inicio</Link>
    </div>
  );

  const initials = (agent.name || "??").split(" ").slice(0, 2).map(s => s[0]).join("").toUpperCase();
  const waHref = agent.phone
    ? `https://wa.me/${digitsOnly(agent.phone).replace(/^\+/, "")}?text=${encodeURIComponent(`Hola ${agent.name}, encontré tu perfil en ROCAFUERTE y me interesa hablar contigo.`)}`
    : null;
  const telHref = agent.phone ? `tel:${digitsOnly(agent.phone)}` : null;
  const mailHref = `mailto:${agent.email}?subject=${encodeURIComponent("Consulta desde ROCAFUERTE")}`;

  return (
    <div className="min-h-[calc(100vh-5rem)]" data-testid="agent-profile-page">
      {/* Hero */}
      <div className="relative bg-gradient-to-b from-[#111111] to-[#0A0A0A] border-b border-[#C8A24A]/10">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
          <Link to="/propiedades" className="text-xs text-[#C8A24A]/60 hover:text-[#C8A24A] inline-flex items-center gap-1" data-testid="agent-back-link">
            <ArrowLeft size={12} /> Ver propiedades
          </Link>
          <div className="mt-6 flex flex-col md:flex-row gap-8 items-start">
            {/* Avatar */}
            <div className="w-28 h-28 md:w-40 md:h-40 rounded-2xl overflow-hidden flex-shrink-0 border-2 border-[#C8A24A]/40 bg-[#1A1A1A] flex items-center justify-center" data-testid="agent-avatar">
              {agent.photo_url ? (
                <img src={imageUrl(agent.photo_url)} alt={agent.name} className="w-full h-full object-cover" />
              ) : (
                <span className="font-heading text-4xl md:text-5xl text-[#C8A24A]">{initials}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-[0.2em] text-[#C8A24A] font-semibold">Asesor ROCAFUERTE</p>
              <h1 className="font-heading text-4xl md:text-5xl mt-2 tracking-tight" data-testid="agent-name">{agent.name}</h1>
              {agent.agency && (
                <Link
                  to={`/agencia/${agent.agency.slug}`}
                  className="mt-2 inline-flex items-center gap-2 text-xs text-[#C8A24A]/80 hover:text-[#C8A24A]"
                  data-testid="agent-agency-badge"
                >
                  <div className="w-5 h-5 rounded-md bg-[#1A1A1A] border border-[#C8A24A]/30 flex items-center justify-center overflow-hidden">
                    {agent.agency.logo_url ? (
                      <img src={imageUrl(agent.agency.logo_url)} alt="" className="w-full h-full object-cover" />
                    ) : null}
                  </div>
                  Miembro de <span className="font-semibold">{agent.agency.name}</span>
                </Link>
              )}
              {agent.specialties?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5" data-testid="agent-specialties">
                  {agent.specialties.map(s => (
                    <span key={s} className="text-xs uppercase tracking-widest px-2.5 py-1 rounded-full bg-[#C8A24A]/10 text-[#C8A24A] border border-[#C8A24A]/30 font-semibold">
                      <Star size={9} weight="fill" className="inline mr-1" /> {s}
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-5 flex flex-wrap gap-2" data-testid="agent-action-row">
                {waHref && (
                  <a
                    href={waHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="agent-wa-btn"
                    className="inline-flex items-center gap-2 h-11 px-4 rounded-xl border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-sm font-semibold"
                  >
                    <WhatsappLogo size={16} weight="duotone" /> WhatsApp
                  </a>
                )}
                {telHref && (
                  <a
                    href={telHref}
                    data-testid="agent-call-btn"
                    className="inline-flex items-center gap-2 h-11 px-4 rounded-xl border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 text-sm font-semibold"
                  >
                    <Phone size={16} weight="duotone" /> Llamar
                  </a>
                )}
                <a
                  href={mailHref}
                  data-testid="agent-mail-btn"
                  className="inline-flex items-center gap-2 h-11 px-4 rounded-xl border border-[#C8A24A]/40 text-[#C8A24A] hover:bg-[#C8A24A]/10 text-sm font-semibold"
                >
                  <Envelope size={16} weight="duotone" /> Correo
                </a>
                <Button
                  variant="outline"
                  onClick={shareProfile}
                  className="h-11 px-4"
                  data-testid="agent-share-btn"
                >
                  <ShareNetwork size={16} className="mr-1" /> Compartir perfil
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowQR(true)}
                  className="h-11 px-4"
                  data-testid="agent-qr-btn"
                >
                  <QrCode size={16} className="mr-1" /> QR
                </Button>
              </div>
              <button
                type="button"
                onClick={copyUrl}
                className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#C8A24A]/60 hover:text-[#C8A24A] font-mono"
                data-testid="agent-copy-url"
              >
                <Copy size={11} /> {publicUrl}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10 lg:py-14 grid md:grid-cols-3 gap-6 md:gap-10">
        {/* Left: meta */}
        <aside className="space-y-6 md:col-span-1">
          {agent.bio ? (
            <section data-testid="agent-bio-section">
              <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold mb-2">Biografía</p>
              <p className="text-sm text-[#C8A24A]/90 leading-relaxed whitespace-pre-wrap">{agent.bio}</p>
            </section>
          ) : null}
          {agent.coverage_zones?.length > 0 && (
            <section data-testid="agent-zones-section">
              <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold mb-2 inline-flex items-center gap-1.5">
                <MapPin size={11} weight="fill" /> Zonas de cobertura
              </p>
              <ul className="flex flex-wrap gap-1.5">
                {agent.coverage_zones.map(z => (
                  <li key={z} className="text-xs px-2.5 py-1 rounded-full bg-[#1A1A1A] border border-[#C8A24A]/20 text-[#C8A24A]/90">
                    {z}
                  </li>
                ))}
              </ul>
            </section>
          )}
          {agent.languages?.length > 0 && (
            <section data-testid="agent-languages-section">
              <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold mb-2 inline-flex items-center gap-1.5">
                <Translate size={11} weight="fill" /> Idiomas
              </p>
              <ul className="flex flex-wrap gap-1.5">
                {agent.languages.map(l => (
                  <li key={l} className="text-xs px-2.5 py-1 rounded-full bg-[#1A1A1A] border border-[#C8A24A]/20 text-[#C8A24A]/90">
                    {l}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </aside>

        {/* Right: properties */}
        <section className="md:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold">Propiedades activas</p>
            <span className="text-xs text-[#C8A24A]/70" data-testid="agent-properties-count">
              {agent.properties_count} publicada{agent.properties_count === 1 ? "" : "s"}
            </span>
          </div>
          {agent.properties?.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#C8A24A]/20 p-10 text-center bg-[#1A1A1A]" data-testid="agent-properties-empty">
              <House size={40} className="mx-auto text-[#C8A24A]/30 mb-3" />
              <p className="font-heading text-lg">Sin propiedades activas por ahora</p>
              <p className="text-xs text-[#C8A24A]/60 mt-1">Vuelve pronto o contacta a {agent.name.split(" ")[0]} para conocer inventario privado.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4" data-testid="agent-properties-grid">
              {agent.properties.map(p => (
                <PropertyCard key={p.id} property={p} />
              ))}
            </div>
          )}
        </section>
      </div>

      {/* QR modal */}
      {showQR && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="agent-qr-modal" onClick={() => setShowQR(false)}>
          <div className="bg-[#111111] border border-[#C8A24A]/30 rounded-2xl p-6 max-w-sm w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold">Compartir por QR</p>
                <p className="font-heading text-xl mt-1">{agent.name}</p>
              </div>
              <button type="button" onClick={() => setShowQR(false)} className="text-[#C8A24A]/70 hover:text-[#C8A24A]" data-testid="agent-qr-close">
                <X size={20} />
              </button>
            </div>
            <div className="bg-white p-4 rounded-xl flex items-center justify-center" data-testid="agent-qr-code">
              <QRCodeSVG value={publicUrl} size={220} level="M" includeMargin={false} />
            </div>
            <p className="mt-4 text-xs text-center text-[#C8A24A]/70 font-mono break-all">{publicUrl}</p>
            <Button
              onClick={copyUrl}
              className="w-full mt-4 bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111] h-11"
              data-testid="agent-qr-copy-btn"
            >
              <Copy size={14} className="mr-1" /> Copiar enlace
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16 animate-pulse">
      <div className="flex gap-6">
        <div className="w-40 h-40 rounded-2xl bg-[#1A1A1A]" />
        <div className="flex-1 space-y-3">
          <div className="h-4 w-32 bg-[#1A1A1A] rounded" />
          <div className="h-10 w-2/3 bg-[#1A1A1A] rounded" />
          <div className="h-4 w-40 bg-[#1A1A1A] rounded" />
        </div>
      </div>
    </div>
  );
}
